import { ID_SHORT_REGEX, JUST_ID_SHORT_REGEX, LAST_INDEX_REGEX } from './AasUtils';

/**
 * Pattern for a valid IdShortPath. It applies to the full string.
 */
export const ID_SHORT_PATH_REGEX = /^[a-z]\w*(\[\d+\])*(\.[a-z]\w*(\[\d+\])*)*$/i;

/**
 * Pattern for a valid IdShortPath segment. It is used to find the segments in
 * a string and contains the group "segment".
 */
export const ID_SHORT_PATH_SEGMENT_REGEX = /(?<segment>[a-z]\w*)(\[\d+\])*/i;

/**
 * Utility class for working with IdShortPaths.
 */
export class IdShortPath {
  private _path: string;

  static validatePath(path: string): boolean {
    return ID_SHORT_PATH_REGEX.test(path);
  }

  static validatePathOrThrow(path: string): void {
    if (!IdShortPath.validatePath(path)) {
      throw new Error(`Invalid IdShortPath: ${path}`);
    }
  }

  constructor(
    path: string,
  ) {
    IdShortPath.validatePathOrThrow(path);
    this._path = path;
  }

  get path(): string {
    return this._path;
  }

  isValid(): boolean {
    return IdShortPath.validatePath(this._path);
  }

  isListItem(): boolean {
    return this._path.endsWith(']');
  }

  hasIdShort(): boolean {
    return !this.isListItem();
  }

  hasParent(): boolean {
    return this._path.includes('.') || this.isListItem();
  }

  isRoot(): boolean {
    return !this.hasParent();
  }

  joinPath(path: string) {
    this._path = `${this._path}.${path}`;
    IdShortPath.validatePathOrThrow(this._path);
  }

  joinIdShort(idShort: string) {
    if (JUST_ID_SHORT_REGEX.test(idShort)) {
      this.joinPath(idShort);
    }
    else {
      throw new Error(`${idShort} is not a valid IdShort.`);
    }
  }

  joinIndex(index: number) {
    this._path = `${this._path}[${index}]`;
    IdShortPath.validatePathOrThrow(this._path);
  }

  get index(): number {
    let index = -1;
    const match = this._path.match(LAST_INDEX_REGEX);
    if (match && match.groups && match.groups.index) {
      index = Number.parseInt(match.groups.index, 10);
    }
    else {
      throw new Error(`${this._path} is not an index element.`);
    }
    return index;
  }

  get segments(): string[] {
    return this._path.split('.');
  }

  get lastSegment(): string {
    return this.segments[this.segments.length - 1];
  }

  get firstSegment(): string {
    return this.segments[0];
  }

  get idShort(): string {
    const lastSegment = this.lastSegment;
    if (JUST_ID_SHORT_REGEX.test(lastSegment)) {
      return lastSegment;
    }
    else {
      throw new Error(`${this._path} is an index element.`);
    }
  }

  get parent(): IdShortPath {
    let parentPath: IdShortPath;
    if (this.hasParent()) {
      if (this.isListItem()) {
        // remove the last index
        const parentPathString = this._path.substring(0, this._path.lastIndexOf('['));
        parentPath = new IdShortPath(parentPathString);
      }
      else {
        // remove the last segment
        const parentPathString = this._path.substring(0, this._path.lastIndexOf('.'));
        parentPath = new IdShortPath(parentPathString);
      }
    }
    else {
      throw new Error(`${this._path} has no parent.`);
    }
    return parentPath;
  }

  get root(): string {
    let idsp = new IdShortPath(this.firstSegment);
    while (idsp.hasParent()) {
      idsp = idsp.parent;
    }
    return idsp.path;
  }

  changeIndex(newIndex: number) {
    if (this.isListItem()) {
      const parentPath = this.parent;
      this._path = `${parentPath._path}[${newIndex}]`;
      IdShortPath.validatePathOrThrow(this._path);
    }
    else {
      throw new Error(`${this._path} is not an index element.`);
    }
  }

  changeIdShort(newIdShort: string) {
    if (JUST_ID_SHORT_REGEX.test(newIdShort)) {
      if (this.hasIdShort()) {
        const parentPath = this.parent;
        this._path = `${parentPath._path}.${newIdShort}`;
        IdShortPath.validatePathOrThrow(this._path);
      }
      else {
        throw new Error(`${this._path} is a list item and has no idShort.`);
      }
    }
    else {
      throw new Error(`${newIdShort} is not a valid IdShort.`);
    }
  }

  get allIdShorts(): string[] {
    const match = this._path.match(ID_SHORT_REGEX);
    let idShorts: string[];
    if (match) {
      idShorts = match.map(m => m[0]);
    }
    else {
      idShorts = [];
    }
    return idShorts;
  }

  removeIndex() {
    if (this.isListItem()) {
      const parentPath = this.parent;
      this._path = parentPath._path;
      IdShortPath.validatePathOrThrow(this._path);
    }
    else {
      throw new Error(`${this._path} is not an index element.`);
    }
  }

  get allIdShortPaths(): string[] {
    const idShortPaths: string[] = [];
    // eslint-disable-next-line ts/no-this-alias
    let idsp: IdShortPath = this;
    while (idsp.hasParent()) {
      idShortPaths.push(idsp.path);
      idsp = idsp.parent;
    }
    idShortPaths.push(idsp.path);
    return idShortPaths.reverse();
  }
}
